<template>
	<div class="address form-group" :class="{ 'is-empty': empty }" :data-element="element.id">
		<div class="d-flex justify-content-between align-items-center">
			<label class="element-label" :class="classes('title')" :title="required ? trans('buttons.required') : ''">
				<span v-html="macro(label)"></span>
				<small v-if="required" class="required">*</small>
			</label>
			<div v-if="fieldVisibleOnlyInPreview" class="app--engagement-advisor-preview-icon ms-2">
				<app-icon :title="trans('visibility.field.preview')" value="eye" type="fas" class="fa-sm" />
			</div>
		</div>
		<div class="row address-row g-2" :class="{ 'is-invalid': !valid || !usps.valid }" ref="fields">
			<div class="col-12" v-if="options('address1')" :class="{ 'is-disabled': readonly('address1') }"
				:title="readonly('address1') ? $t('app.common.readonly') : ''">
				<input type="text" class="form-control" maxlength="128" v-model="value.address1" @focus="toggle"
					@blur="update" data-name="address1" :class="classes('input')" :data-label="labels('address1')"
					:disabled="readonly('address1')" :placeholder="labels('address1')" :readonly="readonly('address1')"
					:required="required">
			</div>
			<div class="col-12" v-if="options('address2')" :class="{ 'is-disabled': readonly('address2') }"
				:title="readonly('address2') ? $t('app.common.readonly') : ''">
				<input type="text" class="form-control" maxlength="128" v-model="value.address2" @focus="toggle"
					@blur="update" data-name="address2" :class="classes('input')" :data-label="labels('address2')"
					:disabled="readonly('address2')" :placeholder="labels('address2')" :readonly="readonly('address2')">
			</div>
			<div class="col-md col-sm-6 col-xs-12" v-if="options('city')" :class="{ 'is-disabled': readonly('city') }"
				:title="readonly('city') ? $t('app.common.readonly') : ''">
				<input type="text" class="form-control" maxlength="64" v-model="value.city" @focus="toggle"
					@blur="update" data-name="city" :class="classes('input')" :data-label="labels('city')"
					:disabled="readonly('city')" :placeholder="labels('city')" :readonly="readonly('city')"
					:required="required">
			</div>
			<div class="col-md col-sm-6 col-xs-12" v-if="options('state') && !states"
				:class="{ 'is-disabled': readonly('state') }"
				:title="readonly('state') ? $t('app.common.readonly') : ''">
				<input type="text" class="form-control" maxlength="64" v-model="value.state" @focus="toggle"
					@blur="update" data-name="state" :class="classes('input')" :data-label="labels('state')"
					:disabled="readonly('state')" :placeholder="labels('state')" :readonly="readonly('state')"
					:required="required">
			</div>
			<div class="col-md col-sm-6 col-xs-12" v-if="options('state') && states"
				:class="{ 'is-disabled': readonly('state') }"
				:title="readonly('state') ? $t('app.common.readonly') : ''">
				<select class="form-select" v-model="value.state" @change="update" @focus="toggle" @blur="update"
					data-name="state" :class="classes('input')" :data-label="labels('state')"
					:disabled="readonly('state')" :placeholder="labels('state')" :readonly="readonly('state')"
					:required="required">
					<option value="">{{ labels('state') }}</option>
					<option v-for="s in states" :value="s.key">{{ s.name }}</option>
				</select>
			</div>
			<div class="col-md col-sm-6 col-xs-12" v-if="options('zip')" :class="{ 'is-disabled': readonly('zip') }"
				:title="readonly('zip') ? $t('app.common.readonly') : ''">
				<input type="text" class="form-control" maxlength="32" v-model="value.zip" @focus="toggle"
					@blur="update" data-name="zip" :class="classes('input')" :data-label="labels('zip')"
					:disabled="readonly('zip')" :placeholder="labels('zip')" :readonly="readonly('zip')"
					:required="required">
			</div>
			<div class="col-md col-sm-6 col-xs-12" v-if="options('country')"
				:class="{ 'is-disabled': readonly('country') }"
				:title="readonly('country') ? $t('app.common.readonly') : ''">
				<select class="form-select" v-model="value.country" @change="update" @focus="toggle" @blur="update"
					data-name="country" :class="classes('input')" :data-label="labels('country')"
					:disabled="readonly('country')" :placeholder="labels('country')" :readonly="readonly('country')"
					:required="required">
					<option value="">{{ labels('country') }}</option>
					<option v-for="c in countries" :value="c.key">{{ c.name }}</option>
				</select>
			</div>
		</div>
		<div class="invalid-feedback" :for="label" :class="{ 'd-block': !valid }">
			<span v-show="!valid">{{ $t('validation.mandatory') }}</span>
		</div>
		<div v-if="!usps.valid" class="invalid-feedback d-block">
			<span>{{ usps.message }}</span>
		</div>
		<small class="form-text" :class="classes('description')">
			<span v-if="description" v-html="macro(description)"></span>&nbsp;
		</small>
		<div v-show="popover.visible && copyenabled && copyitems" ref="popover" class="app--popup popper" role="tooltip"
			width="200">
			<div class="p-content">
				<div class="header">
					{{ $t('front.buttons.copy_address') }}
					<button @click="toggle(false)">
						<app-icon value="close"></app-icon>
					</button>
				</div>
				<div class="content">
					<button v-for="(c, i) in copyitems" type="button" :data-index="i" @click.stop.prevent="copy(i)">
						{{ c.owner }}'s {{ c.label }}
					</button>
				</div>
			</div>
			<div class="p-arrow" data-popper-arrow></div>
		</div>
	</div>
</template>

<script>
import { createPopper } from '@popperjs/core'
import store from '../../store'

export default {
	props: {
		definition: { type: Object, required: true },
		element: { type: Object, required: true },
	},
	computed: {
		copyenabled() {
			return this.options('copy') && this.empty
		},
		copyitems() {
			return store.getters['address'](this.definition, this.element)
		},
		countryonly() {
			return this.fields.indexOf('country') !== -1 && this.fields.length == 1
		},
		description() {
			return typeof this.definition.description == 'string' ? this.definition.description : null
		},
		empty() {
			return !this.countryonly && this.fields.filter(i => {
				return i != 'country' && this.value[i] && this.value[i].length > 0
			}).length == 0
		},
		fields() {
			return ['address1', 'address2', 'city', 'state', 'zip', 'country'].filter(i => this.options(i))
		},
		label() {
			return this.definition.title
		},
		printing() {
			return store.state.printing
		},
		required() {
			return this.options('required') == true
		},
		states() {
			let result = []
			if (this.value && this.value.country) {
				result = Countries.states(this.value.country)
			} else if (this.value && !this.value.country && this.options('country') == false) {
				result = Countries.states('US')
			}
			return result.length > 0 ? result : null
		},
		valid() {
			return !(this.element && this.element.valid === false)
		},
		fieldVisibleOnlyInPreview() {
			return this.definition.print_visibility == 'preview' && store.state.preview == true && store.state.complete == false
		}
	},
	data() {
		return {
			countries: Countries.all(),
			value: { address1: '', address2: '', city: '', state: '', zip: '', country: '' },
			popover: { visible: false, el: null },
			focused: false,
			usps: { message: '', valid: true },
		}
	},
	watch: {
		'element.value': {
			immediate: true,
			handler(value) {
				if (value && value instanceof Object) {
					this.fields.forEach(f => {
						this.value[f] = value[f] ? value[f].toString() : ''
					})
				}
				if (!this.value.country && this.fields.length > 1) {
					this.value.country = store.getters.country.toString()
				}
				if (this.popover.el) {
					this.popover.visible = false
					this.popover.el.update()
				}
			}
		}
	},
	mounted() {
		if (this.options('copy')) {
			this.popover.el = createPopper(this.$refs.fields, this.$refs.popover, {
				placement: 'top',
				modifiers: [
					{ name: 'flip', options: { fallbackPlacements: ['top', 'bottom'] } },
					{ name: 'offset', options: { offset: [0, 8] } },
					{ name: 'arrow', options: { padding: 5 } },
				]
			})
		}
	},
	methods: {
		classes(index) {
			return store.getters['classes'](index)
		},
		copy(index) {
			this.value = this.copyitems[index].value;
			this._usps().then(response => {
				if (response == true) {
					let gk = this.element.group + '|' + this.definition.group
					store.commit('QUEUE', {
						id: this.element.id,
						group: gk,
						value: JSON.parse(JSON.stringify(this.value))
					})
				}
			})
		},
		has(key) {
			return this.options('key') && this.value[key]
		},
		labels(key) {
			if (this.printing) return ''
			return this.options('labels') && this.options('labels')[key]
				? this.options('labels')[key] : this.$t('app.address.' + key)
		},
		macro(value) {
			return store.getters['macro'](value)
		},
		options(value) {
			return store.getters['options'](this.definition, value)
		},
		readonly(key) {
			let v = this.element.value
			return store.getters.readonly(this.definition, v && v.hasOwnProperty(key) && v[key] && v[key].length > 0)
		},
		toggle(show = true) {
			this.queue = false
			this.focused = true
			if (this.popover.el && this.copyitems.length > 0) {
				this.popover.visible = show
				this.popover.el.update()
			}
		},
		update(event) {
			this.focused = false
			setTimeout(() => {
				if (!this.focused) {
					if (this.element.value == null && this.empty) {
						return
					}
					let id = event.target.getAttribute('data-name')
					let equal = JSON.stringify(this.value) === JSON.stringify(this.element.value)
					if (!this.readonly(id) && this.$validate(event) && !equal) {
						if (id == 'country' && this.element.value && this.element.value.country != this.value.country) {
							this.value.state = ''
						}
						this._usps().then(response => {
							if (response == true) {
								let gk = this.element.group + '|' + this.definition.group
								store.commit('QUEUE', {
									id: this.element.id,
									group: gk,
									value: JSON.parse(JSON.stringify(this.value))
								})
							}
						})
					}
				}
			}, 500)
		},
		_usps() {
			let els = this.$el.querySelectorAll('input, select')
			els.forEach(el => el.classList.remove('is-invalid', 'is-valid'))

			if (this.value.country != 'US' || this.options('validate_usps') == false) {
				this.usps.message = ''
				this.usps.valid = true
				return new Promise(function (resolve) {
					resolve(true)
				})
			}

			return axios.post('/app/validate/usps', this.value).then(response => {
				if (response.data.valid == false) {
					let error = response.data.error
					this.usps.message = this.$t('validation.address_usps', { attribute: this.label, error: error })
					this.usps.valid = false
					els.forEach(el => el.classList.add('is-invalid'))
					return false
				}

				if (response.data.address) {
					if (this.value.address1.length == 0) this.value.address1 = response.data.address.address1
					if (this.value.address2.length == 0) this.value.address2 = response.data.address.address2
					this.value.city = response.data.address.city
					this.value.zip = response.data.address.zip
					this.value.state = response.data.address.state
				}

				this.usps.valid = true
				this.usps.message = ''

				return true
			})
		},
		trans(key) {
			let trans = store.getters.trans(key)
			return trans ? trans : this.$t('front.' + key)
		}
	}
}
</script>
